import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import AnnotationDisplayOptions from 'models/data/annotation-display-filter-options-model';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import {
  DISPLAY_ANNOTATION_SUMMARY_TAB_ARRAY,
  renderTooltipTextBySelection,
  annotations,
} from 'constants/feature/annotation-display-filter-constants';
import { ReactComponent as ExpandedIcon } from 'icons/annotation-expanded.svg';
import { ReactComponent as CollapsedIcon } from 'icons/annotation-collapsed.svg';
import ToolTip from 'components/common/tool-tip-component';
import StatementSummaryElementsDisplay from './statement-summary-elements-display';
import { ReactComponent as SelectedNoteIcon } from 'icons/no-border-note-icon.svg';
import { ReactComponent as UnselectedNoteIcon } from 'icons/open-note.svg';
import { ReactComponent as ExpandAllElementsDefault } from 'icons/expand-all-elements-default.svg';
import { ReactComponent as ExpandAllElementsActive } from 'icons/expand-all-elements-active.svg';
import { cancelBatchUpdatePanel } from 'store/actions/batch-panel-actions';
import { clearModeIfPossible } from 'store/actions/modes-actions';
import { setWorkpaperFilterReset } from 'store/actions/annotation-display-actions';
import Permissions from 'permissions/permissions';
import {
  AUTO_TIEOUT_COMPARE_MODE,
  TRAIL_BALANCE_MODE,
} from 'constants/feature/statement-content-constants';
import { WORKPAPER_TYPE } from 'constants/feature/element-panel-constants';
import WorkpaperFilterDropdownComponent from './workpaper-filter-dropdown-component';

export const STATEMENT_SUMMARY_ANNOTATIONS_BLOCK =
  'statement-summary-annotations';
export const STATEMENT_SUMMARY_ANNOTATIONS_BLOCK_ID =
  'statement-summary-annotations-id';
export const EXPAND_SELECTED_ELEMENTS_ID = 'expand-selected-elements';
const ANNOTATION_SUMMARY_ICON_SIZE = '25px';
const ANNOTATION_ICON_NOTE_ELEMENT = 'note';
const StatementSummaryAnnotationsComponent = ({
  setAnnotationDisplayOption,
  disabled,
  disableAllElementsCallout,
  displaySummaryOptions,
  blacklineViewShowElementMode,
  setHideOrDisplayElementsOnStatementPage,
  displayElementDetailsOnStatementPage,
  setBatchModeSelectedIfPossible,
  selectedProjectId,
  selectedStatement,
  showBatchPanelAction,
  annotationDisplayOptions,
  cancelBatchUpdatePanel,
  clearBatchModeIfPossible,
  stateData,
  isSideBySideView = false,
  shouldShowFilter = false,
  viewMode,
  linkedStatements,
  workpaperDetails,
  tbWorkFlowStatus,
  setWorkpaperFilterReset,
}) => {
  const [displayOptions, setdisplayOptions] = useState(
    new AnnotationDisplayOptions(),
  );
  const [workpaperList, setWorkpaperList] = useState([]);
  const workPaperTypeToShow =
    viewMode === TRAIL_BALANCE_MODE
      ? WORKPAPER_TYPE.AUTO_TIEOUT_TB
      : viewMode === AUTO_TIEOUT_COMPARE_MODE
        ? WORKPAPER_TYPE.AUTO_TIEOUT
        : null;

  // Sync local state with Redux annotationDisplayOptions
  useEffect(() => {
    if (annotationDisplayOptions) {
      setdisplayOptions(annotationDisplayOptions);
    }
  }, [annotationDisplayOptions]);

  const handleChange = (option) => {
    let updatedOptions = displayOptions.merge({
      [option]: !displayOptions[option],
    });
    updatedOptions.expandAllElements =
      annotationDisplayOptions.expandAllElements;
    setdisplayOptions(updatedOptions);
    setAnnotationDisplayOption(updatedOptions);
  };

  useEffect(() => {
    if (workPaperTypeToShow) {
      if (viewMode === AUTO_TIEOUT_COMPARE_MODE) {
        setWorkpaperList(
          linkedStatements?.statementList?.map((statement) => ({
            referenceNumber: statement?.referenceNumber,
            workpaperRefId: statement?.workpaperId,
          })) || [],
        );
      } else {
        setWorkpaperList(
          tbWorkFlowStatus?.isCompleted && workpaperDetails?.referenceNumber
            ? [
                {
                  referenceNumber: workpaperDetails?.referenceNumber,
                  workpaperRefId: workpaperDetails?.workpaperRefId,
                },
              ]
            : [],
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, linkedStatements, workpaperDetails, tbWorkFlowStatus]);

  const _setBatchModeWithBannerAndOpenBatchPanel = () => {
    setBatchModeSelectedIfPossible(); // setElementSelectModeBatchWithBannerAction action inside modes-action.js
    showBatchPanelAction();
  };

  const _userCanBatch = Permissions.Element.canEditElement(
    stateData,
    selectedProjectId,
  );
  const readOnly = selectedStatement.isReadOnly();

  const showAllDisabled = disabled && !blacklineViewShowElementMode;

  const renderAnnotationIcon = (item) => {
    return (
      displaySummaryOptions[item.name] && (
        <div
          key={item.id}
          id={item.id}
          className={classNames(
            `${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element`,
            {
              [`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element-unselected`]:
                !displayOptions[item.name],
              [`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element-selected`]:
                displayOptions[item.name],
              [`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element-unselected--disabled`]:
                !displayOptions[item.name] && disabled,
              [`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element-selected--disabled`]:
                displayOptions[item.name] && disabled,
            },
          )}
          onClick={() => !disabled && handleChange(item.name)}
        >
          {item.name === ANNOTATION_ICON_NOTE_ELEMENT ? (
            <ToolTip
              id={`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK_ID}-note`}
              text={
                disabled
                  ? null
                  : displayOptions[item.name]
                    ? 'statement-summary.note.hide.label'
                    : 'statement-summary.note.show.label'
              }
              position="top"
            >
              <div
                className={classNames(
                  `${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element`,
                  `${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element--icon`,
                  {
                    [`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element--annotation-icon--disabled`]:
                      disabled,
                  },
                )}
              >
                {displayOptions[item.name] ? (
                  <SelectedNoteIcon height={18} width={18} />
                ) : (
                  <UnselectedNoteIcon height={18} width={18} />
                )}
              </div>
            </ToolTip>
          ) : (
            <ToolTip
              id={`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK_ID}-${item.name}`}
              text={
                disabled
                  ? null
                  : renderTooltipTextBySelection({
                      name: item.name,
                      isOptionSelected: displayOptions[item.name],
                      isSideBySideView: false,
                    })
              }
              position="top"
            >
              <div>
                <FormattedMessage id={item.label} />
              </div>
            </ToolTip>
          )}
        </div>
      )
    );
  };

  return (
    <div className={`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}`}>
      <div
        className={classNames(
          `${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element`,
          `${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element--annotation-icon`,
          {
            [`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element--annotation-icon--disabled`]:
              showAllDisabled,
          },
        )}
        onClick={() => {
          if (!showAllDisabled) {
            let updatedOptions = displayOptions.merge({
              showAll: !displayOptions['showAll'],
            });
            if (updatedOptions.showAll) {
              let options = new AnnotationDisplayOptions();
              options.showAll = true;
              options.expandAllElements =
                annotationDisplayOptions.expandAllElements;
              if (isSideBySideView) {
                options.workpapersSelected = displayOptions.workpapersSelected;
                options.workpaper = !displayOptions.workpaper ? true : displayOptions.workpaper;
                !displayOptions.workpaper && setWorkpaperFilterReset(true);
              }
              setdisplayOptions(options);
              setAnnotationDisplayOption(options);
              disableAllElementsCallout({ showCallout: true });
            } else {
              setdisplayOptions(updatedOptions);
              setAnnotationDisplayOption(updatedOptions);
              disableAllElementsCallout({ showCallout: false });
            }
          }
        }}
      >
        <ToolTip
          id={
            showAllDisabled
              ? null
              : displayOptions.showAll
                ? `${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK_ID}-collapse`
                : `${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK_ID}-expand`
          }
          text={
            displayOptions.showAll
              ? 'statement-summary-annotaions-collapse-all'
              : 'statement-summary-annotaions-expand-all'
          }
          position={'top'}
        >
          <div>
            {displayOptions.showAll ? (
              <ExpandedIcon
                width={ANNOTATION_SUMMARY_ICON_SIZE}
                height={ANNOTATION_SUMMARY_ICON_SIZE}
              />
            ) : (
              <CollapsedIcon
                width={ANNOTATION_SUMMARY_ICON_SIZE}
                height={ANNOTATION_SUMMARY_ICON_SIZE}
              />
            )}
          </div>
        </ToolTip>
      </div>
      {!isSideBySideView && (
        <>
          <div
            className={classNames(
              `${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element`,
              `${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element--annotation-icon`,
              {
                [`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element--annotation-icon--disabled`]:
                  !_userCanBatch || readOnly || disabled,
                [`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element--annotation-icon--enabled`]:
                  annotationDisplayOptions.expandAllElements,
              },
            )}
            onClick={() => {
              if (!showAllDisabled) {
                if (_userCanBatch && !readOnly && !disabled) {
                  if (annotationDisplayOptions.expandAllElements) {
                    cancelBatchUpdatePanel();
                    clearBatchModeIfPossible();
                  }
                  setAnnotationDisplayOption({
                    ...annotationDisplayOptions,
                    expandAllElements:
                      !annotationDisplayOptions.expandAllElements,
                  });
                  !annotationDisplayOptions.expandAllElements &&
                    _setBatchModeWithBannerAndOpenBatchPanel();
                }
              }
            }}
          >
            <ToolTip
              id={
                showAllDisabled
                  ? null
                  : `${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK_ID}-${EXPAND_SELECTED_ELEMENTS_ID}`
              }
              text={'statement-summary-annotations.expand-all-elements'}
              position={'top'}
            >
              <div>
                {annotationDisplayOptions.expandAllElements ? (
                  <ExpandAllElementsActive
                    className={`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element-annotation-icon`}
                    width={ANNOTATION_SUMMARY_ICON_SIZE}
                    height={ANNOTATION_SUMMARY_ICON_SIZE}
                  />
                ) : (
                  <ExpandAllElementsDefault
                    className={`${STATEMENT_SUMMARY_ANNOTATIONS_BLOCK}__element-annotation-icon`}
                    width={ANNOTATION_SUMMARY_ICON_SIZE}
                    height={ANNOTATION_SUMMARY_ICON_SIZE}
                  />
                )}
              </div>
            </ToolTip>
          </div>
          <StatementSummaryElementsDisplay
            displayElementDetailsOnStatementPage={
              displayElementDetailsOnStatementPage
            }
            setHideOrDisplayElementsOnStatementPage={
              setHideOrDisplayElementsOnStatementPage
            }
            displayOptions={displayOptions}
          />
        </>
      )}
      {(!isSideBySideView || shouldShowFilter) &&
        DISPLAY_ANNOTATION_SUMMARY_TAB_ARRAY.map((item) => {
          // Use new WorkpaperFilterDropdownComponent for workpaper with dropdown functionality
          if (
            displaySummaryOptions[item.name] &&
            isSideBySideView &&
            item.name === annotations.workpaper &&
            workPaperTypeToShow
          ) {
            return (
              <WorkpaperFilterDropdownComponent
                key={item.id}
                item={item}
                displayOptions={displayOptions}
                setAnnotationDisplayOption={(options) => {
                  setdisplayOptions(options);
                  setAnnotationDisplayOption(options);
                }}
                workpaperList={workpaperList}
                workPaperTypeToShow={workPaperTypeToShow}
                disabled={disabled}
                isSideBySideView={isSideBySideView}
              />
            );
          }
          return renderAnnotationIcon(item);
        })}
    </div>
  );
};

const mapStateToProps = ({
  data,
  ui: {
    sideBySideView: {
      autoTieout: { linkedStatements, workpaperDetails },
      trialBalance: { tbWorkFlowStatus },
    },
    statementPage: {
      modes: { contentViewMode },
    },
  },
}) => {
  const {
    selectedProject: { id: selectedProjectId },
    selectedStatement,
  } = data;
  return {
    contentViewMode,
    selectedProjectId,
    selectedStatement,
    stateData: data,
    linkedStatements,
    workpaperDetails,
    tbWorkFlowStatus,
  };
};

const mapDispatchToProps = {
  cancelBatchUpdatePanel,
  clearBatchModeIfPossible: clearModeIfPossible,
  setWorkpaperFilterReset,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(StatementSummaryAnnotationsComponent);
